import { observer } from 'mobx-react-lite';
import type CalendarEvent from 'owa-calendar-types/lib/types/CalendarEvent';
import * as React from 'react';
import DataRow from 'owa-calendar-forms-common/lib/components/DataRow';
import { ControlIcons } from 'owa-control-icons';
import { FluidOwaSource, isFluidEnabledForSource } from 'owa-fluid-validations';
import { Link } from '@fluentui/react/lib/Link';
import generateLinkStyle from 'owa-component-styling-utils/lib/utils/colorSchemesToStyles/generateLinkStyle';
import type { ColorScheme } from 'owa-color-utils/lib/ColorScheme';
import { showCollabSpace } from './PeekEventContent.locstring.json';
import loc from 'owa-localize';
import { logUsage, DatapointStatus, PerformanceDatapoint } from 'owa-analytics';
import type { ReadingPaneSection } from 'owa-calendar-helpers-types/lib/ReadingPaneSection';
import { useQuery } from '@apollo/client';
import { CollabObjectsDocument, initCollabObjectPerfDatapoint } from 'owa-calendar-collab-object';
import type { CollabSpaceData } from 'owa-calendar-types';
import {
    lazyParseCollabObject,
    shouldQueryCollabObject,
} from 'owa-calendar-collaborative-space-utils';
import { TraceErrorObject } from 'owa-trace';
import { toJS } from 'mobx';
import { isSeriesInstance } from 'owa-calendar-event-capabilities';

import styles from './CalendarItemPeek.scss';

interface PeekEventCollabSpaceRowProps {
    event: CalendarEvent;
    colorScheme: ColorScheme;
    onOpenEventClicked: (
        ev?: React.MouseEvent<HTMLElement>,
        scrollToSection?: ReadingPaneSection
    ) => void;
}

export default observer(function PeekEventCollabSpaceRow(props: PeekEventCollabSpaceRowProps) {
    const { event, colorScheme, onOpenEventClicked } = props;
    const [skipQuery, setSkipQuery] = React.useState<boolean>(true);
    const collabObjectEnabled = shouldQueryCollabObject(event);
    const [collabSpaceData, setCollabSpaceData] = React.useState<CollabSpaceData>(null);
    const collabObjectDP = React.useRef<PerformanceDatapoint>(
        collabObjectEnabled ? initCollabObjectPerfDatapoint('PeekEventCollabSpaceRow') : null
    );

    const { error, data } = useQuery(CollabObjectsDocument, {
        variables: {
            id: event.UID,
            organizerEmail: event.Organizer?.Mailbox?.EmailAddress,
            userEmail: event.ItemId?.mailboxInfo?.userIdentity,
            resourcesToBeFetched: ['notes'],
        },
        fetchPolicy: 'no-cache',
        skip: skipQuery,
        onCompleted: () => {
            collabObjectDP?.current?.end();

            lazyParseCollabObject
                .importAndExecute(
                    data?.collabObjects?.resources,
                    event.CollabSpace,
                    data?.collabObjects?.changeKey,
                    event.IsRecurring,
                    isSeriesInstance(event)
                )
                .then(parsedCollab => {
                    setCollabSpaceData(parsedCollab);
                });
        },
        onError: error => {
            collabObjectDP?.current?.endWithError(
                DatapointStatus.RequestNotComplete,
                error as TraceErrorObject
            );
        },
    });

    React.useEffect(() => {
        setSkipQuery(!collabObjectEnabled);
    }, []);

    React.useEffect(() => {
        if (event.CollabSpace && (!collabObjectEnabled || error)) {
            setCollabSpaceData(toJS(event.CollabSpace));
        }
    }, [error, event.CollabSpace]);

    const onClick = (ev?: React.MouseEvent<HTMLElement>) => {
        logUsage('CollabSpaceOpenedFromPeekLink', undefined, { isCore: true });
        onOpenEventClicked(ev, 'CollabSpace');
    };

    if (
        isFluidEnabledForSource(FluidOwaSource.CalendarReadingPane) &&
        collabSpaceData?.shareLinkInfo?.url &&
        collabSpaceData?.enabled &&
        collabSpaceData?.isShared === 'false'
    ) {
        return (
            <DataRow
                className={styles.collabSpaceRow}
                iconName={ControlIcons.SharedNotes}
                iconClassName={styles.collabSpaceIcon}>
                <Link
                    styles={generateLinkStyle(colorScheme)}
                    onClick={onClick}
                    className={styles.collabSpaceLink}>
                    {loc(showCollabSpace)}
                </Link>
            </DataRow>
        );
    }

    return null;
});
